import { createFileRoute, Link, useCanGoBack, useRouter } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useQuery, useQueryClient, useMutation } from "@tanstack/react-query";
import {
  AlertOctagon,
  AlertTriangle,
  ArrowLeft,
  Building2,
  CalendarClock,
  Clock,
  FileCheck2,
  Leaf,
  Loader2,
  Lock,
  PackageSearch,
  Save,
  ScaleIcon,
  Trophy,
  Users,
  Wallet,
} from "lucide-react";
import { toast } from "sonner";
import { AppShell } from "@/components/layout/AppShell";
import { EmptyState } from "@/components/intelligence/EmptyState";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { compraAgilService } from "@/services/compra-agil.service";
import { estadoLabels, formatCLP } from "@/lib/mock-data";
import { textoCierre } from "@/lib/compra-agil-signals";
import { evaluarElegibilidad, puedeMoverAEtapa } from "@/lib/licitacion-elegibilidad";
import { cn } from "@/lib/utils";
import type { CompraAgil, LicitacionEstado, Prioridad } from "@/types";

function NotFoundCompraAgil() {
  return (
    <AppShell title="Compra Ágil no encontrada">
      <EmptyState
        icon={AlertTriangle}
        title="No encontramos este proceso"
        description="Puede que el código no exista o ya no esté disponible."
        action={
          <Link to="/compra-agil" className="text-sm text-primary hover:underline">
            Volver al listado
          </Link>
        }
      />
    </AppShell>
  );
}

export const Route = createFileRoute("/compra-agil/$id")({
  head: () => ({
    meta: [{ title: "Detalle Compra Ágil — LicitaPro" }],
  }),
  notFoundComponent: NotFoundCompraAgil,
  errorComponent: NotFoundCompraAgil,
  component: CompraAgilDetalle,
});

/**
 * Vuelve al listado por el historial del navegador, para caer en la misma
 * página con los filtros que el usuario traía (viven en la URL, ver
 * `CompraAgilSearch` en compra-agil.index.tsx). Un `<Link to="/compra-agil">`
 * fijo perdería esos filtros. Si no hay historial al que volver (el detalle
 * se abrió directo por URL, o en pestaña nueva) cae al enlace normal.
 */
function VolverAlListado() {
  const router = useRouter();
  const puedeVolver = useCanGoBack();
  const clase =
    "inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground";

  if (!puedeVolver) {
    return (
      <Link to="/compra-agil" className={clase}>
        <ArrowLeft className="size-4" /> Volver
      </Link>
    );
  }
  return (
    <button type="button" onClick={() => router.history.back()} className={clase}>
      <ArrowLeft className="size-4" /> Volver
    </button>
  );
}

function fmtFecha(iso: string | null): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleString("es-CL", { timeZone: "America/Santiago" });
}

function Section({
  title,
  icon: Icon,
  children,
}: {
  title: string;
  icon: React.ComponentType<{ className?: string }>;
  children: React.ReactNode;
}) {
  return (
    <div className="rounded-xl border border-border bg-card p-5 shadow-elegant">
      <h3 className="font-display font-semibold mb-3 flex items-center gap-1.5">
        <Icon className="size-4 text-primary" /> {title}
      </h3>
      {children}
    </div>
  );
}

/** Tarjeta de la barra resumen — mismo lenguaje que KpiTile (Órdenes de Compra):
 * borde + fondo tenue propio, para que cada cifra se lea como una unidad aparte
 * en vez de perderse en una fila continua. */
function HeroStat({
  icon: Icon,
  label,
  value,
  hint,
  tone,
}: {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  value: React.ReactNode;
  hint?: React.ReactNode;
  tone?: "warning" | "destructive";
}) {
  return (
    <div
      className={cn(
        "rounded-lg border p-3 flex items-center gap-3 h-full",
        tone === "destructive"
          ? "border-destructive/30 bg-destructive/5"
          : tone === "warning"
            ? "border-warning/30 bg-warning/5"
            : "border-border bg-muted/30",
      )}
    >
      <div
        className={cn(
          "size-9 rounded-lg grid place-items-center shrink-0",
          tone === "destructive"
            ? "bg-destructive/10 text-destructive"
            : tone === "warning"
              ? "bg-warning/10 text-warning"
              : "bg-primary/10 text-primary",
        )}
      >
        <Icon className="size-4" />
      </div>
      <div className="min-w-0">
        <div className="text-[11px] uppercase tracking-wide text-muted-foreground">{label}</div>
        <div
          className={cn(
            "font-display text-lg font-semibold tabular-nums leading-tight truncate",
            tone === "destructive" ? "text-destructive" : tone === "warning" ? "text-warning" : "text-foreground",
          )}
        >
          {value}
        </div>
        {hint && <div className="text-[11px] text-muted-foreground truncate">{hint}</div>}
      </div>
    </div>
  );
}

const PRIORIDADES: Prioridad[] = ["alta", "media", "baja"];
const ESTADOS_PIPELINE = Object.keys(estadoLabels) as LicitacionEstado[];

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="space-y-1.5 text-xs font-medium text-muted-foreground">
      <span>{label}</span>
      {children}
    </label>
  );
}

/**
 * Mismo propósito que "Gestión interna" en LicitacionDetail.tsx, adaptado a
 * Compra Ágil: sin esto, seguir una Compra Ágil no daba ningún valor más
 * allá de aparecer en el kanban de /crm — no había forma de asignar
 * responsable, prioridad ni dejar notas desde el propio detalle.
 */
function GestionInternaPanel({ ca, onSaved }: { ca: CompraAgil; onSaved: (tracking: NonNullable<CompraAgil["tracking"]>) => void }) {
  const tracking = ca.tracking;
  const [estado, setEstado] = useState<LicitacionEstado>((tracking?.estadoPipeline as LicitacionEstado) ?? "detectada");
  const [prioridad, setPrioridad] = useState<Prioridad>((tracking?.prioridad as Prioridad) ?? "media");
  const [responsable, setResponsable] = useState(tracking?.responsable ?? "");
  const [notas, setNotas] = useState(tracking?.notas ?? "");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setEstado((tracking?.estadoPipeline as LicitacionEstado) ?? "detectada");
    setPrioridad((tracking?.prioridad as Prioridad) ?? "media");
    setResponsable(tracking?.responsable ?? "");
    setNotas(tracking?.notas ?? "");
  }, [tracking]);

  if (!tracking) return null;

  // evaluarElegibilidad()/puedeMoverAEtapa() son compartidas con Licitaciones
  // (crm.tsx, LicitacionDetail.tsx) — aceptan {estadoMP, fechaCierre}, así
  // que acá se les pasa estadoCodigo bajo ese nombre.
  const elegible = { estadoMP: ca.estadoCodigo, fechaCierre: ca.fechaCierre };
  const elegibilidad = evaluarElegibilidad(elegible);

  const save = async () => {
    if (saving) return;
    const regla = puedeMoverAEtapa(elegible, estado);
    if (!regla.permitido) {
      toast.error(`No se puede pasar a ${estadoLabels[estado]}`, { description: regla.motivo });
      return;
    }
    setSaving(true);
    try {
      const next = await compraAgilService.updateTracking(ca.id, {
        estadoPipeline: estado,
        prioridad,
        responsable: responsable.trim(),
        notas: notas.trim(),
      });
      onSaved(next);
      toast.success("Gestión interna actualizada");
    } catch {
      toast.error("No fue posible guardar los cambios");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Section title="Gestión interna" icon={FileCheck2}>
      {!elegibilidad.postulable && (
        <p className="mb-3 flex items-start gap-2 rounded-md border border-border bg-muted/60 px-3 py-2 text-xs text-muted-foreground">
          <Lock className="mt-0.5 size-3.5 shrink-0" />
          <span>{elegibilidad.motivo} Puedes seguir la Compra Ágil para consulta, pero no avanzar etapas de oferta.</span>
        </p>
      )}
      <div className="grid gap-3 mb-3">
        <Field label="Estado del pipeline">
          <Select value={estado} onValueChange={(value) => setEstado(value as LicitacionEstado)}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {ESTADOS_PIPELINE.map((item) => (
                <SelectItem key={item} value={item} disabled={!puedeMoverAEtapa(elegible, item).permitido && item !== estado}>
                  {estadoLabels[item]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Field>
        <Field label="Prioridad">
          <Select value={prioridad} onValueChange={(value) => setPrioridad(value as Prioridad)}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {PRIORIDADES.map((item) => (
                <SelectItem key={item} value={item} className="capitalize">
                  {item}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Field>
        <Field label="Responsable">
          <Input
            value={responsable}
            maxLength={120}
            onChange={(event) => setResponsable(event.target.value)}
            placeholder="Sin asignar"
          />
        </Field>
      </div>
      <Field label="Notas">
        <Textarea
          value={notas}
          maxLength={4000}
          onChange={(event) => setNotas(event.target.value)}
          placeholder="Acuerdos, riesgos y próximos pasos"
        />
      </Field>
      <Button size="sm" className="mt-3" onClick={() => void save()} disabled={saving}>
        <Save />
        {saving ? "Guardando…" : "Guardar gestión"}
      </Button>
    </Section>
  );
}

export function CompraAgilDetalle() {
  const { id } = Route.useParams();
  const queryClient = useQueryClient();

  const detalle = useQuery({
    queryKey: ["compra-agil", id],
    queryFn: () => compraAgilService.detalle(id),
    retry: false,
    // El backend hidrata productos/proveedores/descripción en segundo plano
    // la primera vez (no bloquea la respuesta) — mientras detalleSyncedAt
    // siga null, reintenta cada 4s hasta que aparezca (máx. ~8 intentos).
    refetchInterval: (query) => {
      const data = query.state.data;
      if (!data || data.detalleSyncedAt) return false;
      return query.state.dataUpdateCount < 8 ? 4000 : false;
    },
  });

  const track = useMutation({
    mutationFn: () => compraAgilService.track(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["compra-agil", id] }),
  });

  if (detalle.isLoading) {
    return (
      <AppShell title="Compra Ágil">
        <Skeleton className="h-24 rounded-xl mb-4" />
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <div className="lg:col-span-2 space-y-4">
            <div className="rounded-xl border border-border bg-card p-5 shadow-elegant space-y-3">
              <Skeleton className="h-4 w-24" />
              <Skeleton className="h-3 w-full" />
              <Skeleton className="h-3 w-5/6" />
            </div>
            <div className="rounded-xl border border-border bg-card p-5 shadow-elegant space-y-3">
              <Skeleton className="h-4 w-40" />
              <Skeleton className="h-3 w-full" />
              <Skeleton className="h-3 w-full" />
              <Skeleton className="h-3 w-2/3" />
            </div>
            <div className="rounded-xl border border-border bg-card p-5 shadow-elegant space-y-3">
              <Skeleton className="h-4 w-48" />
              <Skeleton className="h-3 w-full" />
              <Skeleton className="h-3 w-3/4" />
            </div>
          </div>
          <div className="space-y-4">
            <div className="rounded-xl border border-border bg-card p-5 shadow-elegant space-y-3">
              <Skeleton className="h-4 w-28" />
              <Skeleton className="h-3 w-full" />
              <Skeleton className="h-3 w-5/6" />
            </div>
            <div className="rounded-xl border border-border bg-card p-5 shadow-elegant space-y-3">
              <Skeleton className="h-4 w-36" />
              <Skeleton className="h-3 w-full" />
              <Skeleton className="h-3 w-2/3" />
            </div>
          </div>
        </div>
      </AppShell>
    );
  }
  if (detalle.isError || !detalle.data) return <NotFoundCompraAgil />;
  const ca = detalle.data;

  const cierre = textoCierre(ca.fechaCierre);
  const motivo =
    ca.estadoCodigo === "cancelada"
      ? ca.motivoCancelacion
      : ca.estadoCodigo === "desierta"
        ? ca.motivoDesierta
        : null;
  const proveedores = ca.proveedoresCotizando ?? [];
  const numProveedores = proveedores.length;
  const maxMonto = Math.max(1, ...proveedores.map((p) => p.monto_total ?? 0));
  const proveedoresOrdenados = [...proveedores].sort(
    (a, b) => (a.monto_total ?? Infinity) - (b.monto_total ?? Infinity),
  );
  const cierreTone = cierre.urgencia === "critica" ? "destructive" : cierre.urgencia === "proxima" ? "warning" : undefined;

  return (
    <AppShell
      title={ca.nombre}
      subtitle={`${ca.codigo} · ${ca.organismoComprador}`}
      actions={
        <div className="flex items-center gap-2">
          {!ca.tracking ? (
            <Button size="sm" onClick={() => track.mutate()} disabled={track.isPending}>
              Seguir esta Compra Ágil
            </Button>
          ) : (
            <span className="text-xs px-2.5 py-1.5 rounded-md bg-muted text-muted-foreground">
              En seguimiento · {estadoLabels[ca.tracking.estadoPipeline as LicitacionEstado] ?? ca.tracking.estadoPipeline}
            </span>
          )}
          <VolverAlListado />
        </div>
      }
    >
      {motivo && (
        <div className="mb-4 flex items-start gap-2.5 rounded-xl border border-warning/30 bg-warning/10 p-4 text-sm text-warning">
          <AlertOctagon className="size-4 mt-0.5 shrink-0" />
          <div>
            <div className="font-medium">
              {ca.estadoCodigo === "cancelada" ? "Proceso cancelado" : "Proceso declarado desierto"}
            </div>
            <p className="text-warning/90 mt-0.5">{motivo}</p>
          </div>
        </div>
      )}

      {!ca.detalleSyncedAt && (
        <div className="mb-4 flex items-center gap-2 text-xs text-muted-foreground">
          <Loader2 className="size-3.5 animate-spin" />
          Cargando el detalle completo (productos, proveedores, descripción)…
        </div>
      )}

      {/* Barra resumen: lo primero que hay que ver, no lo último. */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
        <HeroStat
          icon={Wallet}
          label="Presupuesto"
          value={ca.montoDisponibleCLP != null ? formatCLP(ca.montoDisponibleCLP) : "No informado"}
        />
        <HeroStat icon={Clock} label="Cierre" value={cierre.texto} hint={cierre.fecha} tone={cierreTone} />
        <HeroStat
          icon={Users}
          label="Cotizando"
          value={numProveedores}
          hint={numProveedores === 1 ? "proveedor" : "proveedores"}
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 space-y-4">
          <Section title="Descripción" icon={PackageSearch}>
            <p className="text-sm text-muted-foreground">{ca.descripcion ?? "Sin descripción."}</p>
          </Section>

          <Section title="Productos solicitados" icon={PackageSearch}>
            {!ca.productosSolicitados || ca.productosSolicitados.length === 0 ? (
              <div className="flex items-center gap-2 py-4 text-sm text-muted-foreground">
                <PackageSearch className="size-4 shrink-0 opacity-50" />
                Sin productos informados.
              </div>
            ) : (
              <ul className="divide-y divide-border">
                {ca.productosSolicitados.map((p, i) => (
                  <li key={i} className="flex items-center justify-between gap-3 py-2.5 first:pt-0 last:pb-0 text-sm">
                    <span className="text-foreground">{p.nombre}</span>
                    <span className="text-muted-foreground tabular-nums shrink-0">
                      {p.cantidad ?? "—"} {p.unidad_medida ?? ""}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </Section>

          <Section title={`Proveedores cotizando (${numProveedores})`} icon={Users}>
            {proveedoresOrdenados.length === 0 ? (
              <div className="flex items-center gap-2 py-4 text-sm text-muted-foreground">
                <Users className="size-4 shrink-0 opacity-50" />
                Sin cotizaciones registradas todavía.
              </div>
            ) : (
              <ul className="space-y-2.5">
                {proveedoresOrdenados.map((p, i) => {
                  const seleccionado = Boolean(p.seleccion?.proveedor_seleccionado);
                  const monto = p.monto_total ?? 0;
                  return (
                    <li
                      key={i}
                      className={cn(
                        "rounded-lg p-2.5",
                        seleccionado ? "bg-success/5 ring-1 ring-success/30" : "bg-muted/30",
                      )}
                    >
                      <div className="flex items-center justify-between gap-2 text-sm mb-1.5">
                        <span
                          className={cn(
                            "truncate flex items-center gap-1.5",
                            seleccionado ? "font-medium text-success" : "text-foreground",
                          )}
                        >
                          {seleccionado && <Trophy className="size-3.5 shrink-0" />}
                          {p.razon_social ?? p.rut_proveedor ?? "—"}
                        </span>
                        <span
                          className={cn(
                            "tabular-nums shrink-0",
                            seleccionado ? "font-semibold text-success" : "text-muted-foreground",
                          )}
                        >
                          {p.monto_total != null ? formatCLP(p.monto_total) : "—"}
                        </span>
                      </div>
                      <div className="h-1.5 rounded-full bg-muted overflow-hidden">
                        <div
                          className={cn("h-full rounded-full", seleccionado ? "bg-success" : "bg-primary/40")}
                          style={{ width: `${Math.max(4, (monto / maxMonto) * 100)}%` }}
                        />
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </Section>
        </div>

        <div className="space-y-4">
          {ca.tracking && (
            <GestionInternaPanel
              ca={ca}
              onSaved={(tracking) =>
                queryClient.setQueryData(["compra-agil", id], { ...ca, tracking })
              }
            />
          )}

          <Section title="Organismo" icon={Building2}>
            <dl className="space-y-2 text-sm">
              <div>
                <dt className="text-muted-foreground text-xs">Organismo comprador</dt>
                <dd>{ca.organismoComprador}</dd>
              </div>
              {ca.unidadCompra && (
                <div>
                  <dt className="text-muted-foreground text-xs">Unidad de compra</dt>
                  <dd>{ca.unidadCompra}</dd>
                </div>
              )}
              {ca.nombreRegion && (
                <div>
                  <dt className="text-muted-foreground text-xs">Región</dt>
                  <dd>{ca.nombreRegion}</dd>
                </div>
              )}
              {ca.direccionEntrega && (
                <div>
                  <dt className="text-muted-foreground text-xs">Dirección de entrega</dt>
                  <dd>{ca.direccionEntrega}</dd>
                </div>
              )}
            </dl>
          </Section>

          <Section title="Fechas y plazos" icon={CalendarClock}>
            <dl className="space-y-2 text-sm">
              <div>
                <dt className="text-muted-foreground text-xs">Publicación</dt>
                <dd>{fmtFecha(ca.fechaPublicacion)}</dd>
              </div>
              <div>
                <dt className="text-muted-foreground text-xs">Cierre</dt>
                <dd>{fmtFecha(ca.fechaCierre)}</dd>
              </div>
              {ca.plazoEntregaDias != null && (
                <div>
                  <dt className="text-muted-foreground text-xs">Plazo de entrega</dt>
                  <dd>{ca.plazoEntregaDias} días</dd>
                </div>
              )}
            </dl>
          </Section>

          {(ca.consideraRequisitosMedioambientales ||
            ca.consideraRequisitosImpactoSocial ||
            (ca.multaSancion ?? 0) > 0) && (
            <Section title="Requisitos especiales" icon={AlertOctagon}>
              <div className="flex flex-wrap gap-1.5">
                {ca.consideraRequisitosMedioambientales && (
                  <span className="inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded-full border border-border text-muted-foreground">
                    <Leaf className="size-3" /> Criterios medioambientales
                  </span>
                )}
                {ca.consideraRequisitosImpactoSocial && (
                  <span className="inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded-full border border-border text-muted-foreground">
                    <Users className="size-3" /> Impacto social
                  </span>
                )}
                {(ca.multaSancion ?? 0) > 0 && (
                  <span className="inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded-full border border-border text-muted-foreground">
                    <ScaleIcon className="size-3" /> Multa por incumplimiento: {formatCLP(ca.multaSancion ?? 0)}
                  </span>
                )}
              </div>
            </Section>
          )}
        </div>
      </div>
    </AppShell>
  );
}
